import { useEffect, useRef } from 'react';
import type { PlaybackRequest } from '../../state/playback-context';
import type { WatchEntry } from '../../watch-progress';
import { recordProgress, resumePosition } from '../../watch-progress';
import { rememberChannel } from '../../channel-memory';
import type { Timeline } from './usePlaybackSession';

const SAVE_EVERY_MS = 10_000;

/**
 * Movies and episodes: starts where the viewer stopped last time, and saves
 * the position while playing and when leaving, for "Continue watching".
 * Live channels: remembered for "Recently watched".
 */
export function useWatchProgress(current: PlaybackRequest, state: string, time: Timeline, seekTo: (seconds: number) => void): void {
  const vod = current.item.kind !== 'live';
  const resumed = useRef<string | null>(null);
  // The latest position of the item playing, once it has resumed (so a stale 0 never overwrites it).
  const snapshot = useRef<WatchEntry | null>(null);
  if (vod && resumed.current === current.item.key) {
    snapshot.current = {
      sourceId: current.item.sourceId,
      item: current.item,
      series: current.series,
      position: time.position,
      duration: Number.isFinite(time.duration) ? time.duration : 0,
      updatedAt: Date.now(),
    };
  }

  // Live channels: once one really plays, it goes first in "Recently watched".
  const remembered = useRef<string | null>(null);
  useEffect(() => {
    if (vod || state !== 'playing' || remembered.current === current.item.key) return;
    remembered.current = current.item.key;
    rememberChannel(current.item);
  }, [vod, state, current.item]);

  // Resume once per item, as soon as it plays.
  useEffect(() => {
    if (!vod || state !== 'playing' || resumed.current === current.item.key) return;
    resumed.current = current.item.key;
    const at = resumePosition(current.item.sourceId, current.item.key);
    if (at !== null) seekTo(at);
  }, [vod, state, current.item, seekTo]);

  // Save while open, and the last position when the item changes or the player closes.
  useEffect(() => {
    if (!vod) return undefined;
    const item = current.item;
    const save = () => {
      const entry = snapshot.current;
      if (entry && entry.item === item) recordProgress({ ...entry, updatedAt: Date.now() });
    };
    const timer = setInterval(save, SAVE_EVERY_MS);
    return () => {
      clearInterval(timer);
      save();
    };
  }, [vod, current.item]);
}
